import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../utils/api';
import { Heart, Mail, ArrowLeft, MailCheck } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', {
        email,
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-8 group">
          <div className="p-2 rounded-xl" style={{ backgroundColor: '#E57A44' }}>
            <Heart className="w-6 h-6" style={{ color: '#FDFBF7', strokeWidth: 2 }} />
          </div>
          <span className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Love Restored</span>
        </Link>

        {sent ? (
          <div className="p-8 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #F0EBE0' }} data-testid="forgot-sent-panel">
            <div className="inline-flex items-center justify-center rounded-full mb-5" style={{ width: 56, height: 56, backgroundColor: '#78A18E20' }}>
              <MailCheck className="w-7 h-7" style={{ color: '#5C715E', strokeWidth: 2 }} />
            </div>
            <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Check your email</h2>
            <p style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', lineHeight: 1.7 }}>
              If an account exists for <strong style={{ color: '#2C2724' }}>{email}</strong>, we've sent a reset link. It expires in <strong>1 hour</strong>. Please check your inbox (and spam folder) and follow the link to choose a new password.
            </p>
            <p className="mt-4 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D', lineHeight: 1.7 }}>
              Didn't get an email? Wait a minute, then <button onClick={() => setSent(false)} className="underline" style={{ color: '#E57A44', fontWeight: 600 }} data-testid="forgot-try-again">try a different email</button>.
            </p>
            <div className="mt-8 pt-6" style={{ borderTop: '1px solid #F0EBE0' }}>
              <Link to="/login" className="inline-flex items-center gap-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="forgot-back-to-login">
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </Link>
            </div>
          </div>
        ) : (
          <div>
            <h2 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Reset your password</h2>
            <p className="mb-8" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
              Enter the email you used to sign up. We'll send you a link to choose a new password.
            </p>

            {error && (
              <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }} data-testid="forgot-error">
                <p style={{ fontFamily: 'Manrope, sans-serif', color: '#991B1B', fontSize: '14px' }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500, fontSize: '14px' }}>Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#8C867D', strokeWidth: 2 }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2"
                    style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6D5B8', color: '#2C2724' }}
                    placeholder="your@email.com"
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full py-4 rounded-xl transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                data-testid="forgot-submit"
              >
                {loading ? 'Sending link…' : 'Send reset link'}
              </button>
            </form>

            <div className="mt-8 pt-6 text-center" style={{ borderTop: '1px solid #F0EBE0' }}>
              <Link to="/login" className="inline-flex items-center gap-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="forgot-back-login">
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
